import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import { toast } from "react-hot-toast";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  Upload,
  Camera,
  FileText,
  Flame,
  Shield,
  HeartPulse,
  Calendar,
  Users,
  Thermometer,
  AlertTriangle,
  Package,
} from "lucide-react";

const SectionESafety = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({
    e1Photo: null,
  });

  const [form, setForm] = useState({
    e1: "",
    e1Date: "",
    e1Photo: null,
    e2: "",
    e3: "",
    remarks: "",
  });

  const [errors, setErrors] = useState({});

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem("survey_section_e");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const { timestamp, canteenInfo, ...formData } = parsedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_e", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));

      // 🔥 CREATE IMAGE PREVIEW
      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);
        setPreviewImages((prev) => ({
          ...prev,
          [name]: previewUrl,
        }));
      }

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }

      // Validate file type
      const allowedTypes2 = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes2.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));

      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.e1) newErrors.e1 = t.global.err_a1;
    if (!form.e2) newErrors.e2 = t.global.err_a1;
    if (!form.e3) newErrors.e3 = t.global.err_a1;

    // File validation only if file IS uploaded (optional but recommended)
    if (form.e1Photo && form.e1Photo.size > 5 * 1024 * 1024) {
      newErrors.e1Photo = t.global.err_file_size;
    }
    if (form.e1Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.e1Photo.type)) {
        newErrors.e1Photo = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Get latitude & longitude automatically
      let latitude = "";
      let longitude = "";

      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              resolve();
            },
            (error) => {
              console.warn("Geolocation error:", error);
              resolve(); // still proceed without location
            },
            { timeout: 10000, enableHighAccuracy: true },
          );
        });
      }

      // Create FormData for API
      const fd = new FormData();
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("latitude", latitude || "0");
      fd.append("longitude", longitude || "0");

      // Section E data mapping
      const yesNoMap = { [t.global.yes]: "1", [t.global.no]: "2" };

      fd.append("sfty_cleans_E1", yesNoMap[form.e1]);

      // Use auto-date for E1 if YES is selected
      if (form.e1 === t.global.yes) {
        const today = new Date().toISOString().split("T")[0];
        fd.append("sfty_cleans_E1_date", today);

        // Use user-provided date if available
        if (form.e1Date) {
          fd.append("sfty_cleans_E1_date", form.e1Date);
        }

        if (form.e1Photo) {
          fd.append("sfty_cleans_E1_upload", form.e1Photo);
        }
      }

      fd.append("sfty_cleans_E2", yesNoMap[form.e2]);
      fd.append("sfty_cleans_E3", yesNoMap[form.e3]);

      if (form.remarks) {
        fd.append("safety_remarks", form.remarks);
      }

      fd.append("current_section", "safetyArngmnt");

      // Dispatch the action
      await dispatch(createKitchenDining(fd));

      // ✅ Check if this section is partial (missing optional uploads/date when YES is selected)
      const isPartial = form.e1 === t.global.yes && (!form.e1Date || !form.e1Photo);

      // ✅ Save to localStorage with partial flag
      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_e", JSON.stringify(sectionData));

      // ✅ Mark Section E as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.e = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Show success message
      setShowSuccess(true);

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      // Save locally for offline
      const isPartial = form.e1 === t.global.yes && (!form.e1Date || !form.e1Photo);

      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_e", JSON.stringify(sectionData));

      // Mark as completed locally
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.e = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section E submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchenCumDining.sections.E.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchenCumDining.sections.E.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-full flex items-center">
                <Shield size={14} className="mr-1" />
                Safety Survey
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.area_id}: ${state.area_name}` : t.global.na}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* E1 - Fire Extinguisher */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Flame size={18} className="mr-2" />
                E.1 {t.modules.kitchenCumDining.sections.E.questions.fireExtinguisher}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.E.hints.fireExtinguisher.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>Fire extinguisher must be properly installed</li>
                  <li>Must be within expiry/refill date</li>
                  <li>Clearly visible and accessible location</li>
                  <li>Staff trained on its usage</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.e1 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.e1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="e1"
                      value={option}
                      checked={form.e1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.e1 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.e1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.e1}
                </p>
              )}

              {form.e1 === t.global.yes && (
                <div className="mt-6 space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  {/* Date Input */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 flex items-center">
                      <Calendar size={14} className="mr-2" />
                      Last refill date of fire extinguisher (Optional but recommended):
                    </label>
                    <input
                      type="date"
                      name="e1Date"
                      value={form.e1Date}
                      onChange={handleChange}
                      className={`w-full border p-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition ${
                        errors.e1Date ? "border-red-500" : "border-gray-300"
                      }`}
                      max={new Date().toISOString().split("T")[0]}
                    />
                    {errors.e1Date && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />{" "}
                        {errors.e1Date}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      If not entered, current date will be used as last refill date
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Note: If not provided, section will be marked as "Partially Filled"
                    </p>
                  </div>

                  {/* Photo Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Upload picture of extinguisher (Optional):
                    </label>

                    {/* CAMERA + UPLOAD BUTTONS */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("e1Photo");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="e1Photo"
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg cursor-pointer font-medium"
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    {/* Hidden Input */}
                    <input
                      type="file"
                      name="e1Photo"
                      id="e1Photo"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                    />

                    {/* Preview Box */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        previewImages.e1Photo
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {previewImages.e1Photo ? (
                        <div className="flex flex-col items-center gap-3">
                          <img
                            src={previewImages.e1Photo}
                            alt="Preview"
                            className="max-h-48 rounded-lg shadow"
                          />

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, e1Photo: null }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                e1Photo: null,
                              }));
                            }}
                            className="text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint}
                        </p>
                      )}
                    </div>

                    {errors.e1Photo && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.e1Photo}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* E2 - First Aid Box */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <HeartPulse size={18} className="mr-2" />
                E.2 {t.modules.kitchenCumDining.sections.E.questions.firstAid}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.E.hints.firstAid.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Check expiry dates of all medicines</li>
                  <li>Must contain disinfectants for cuts and burns</li>
                  <li>Essential medicines for kitchen injuries</li>
                  <li>Properly sealed and accessible</li>
                  <li>Clearly labeled and stored appropriately</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.e2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.e2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="e2"
                      value={option}
                      checked={form.e2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.e2 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.e2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.e2}
                </p>
              )}
            </div>

            {/* E3 - Staff Training */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Users size={18} className="mr-2" />
                E.3 {t.modules.kitchenCumDining.sections.E.questions.fireTraining}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.E.hints.fireTraining.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>Regular fire safety training sessions</li>
                  <li>Conducted mock drills at least twice a year</li>
                  <li>All staff aware of emergency exits</li>
                  <li>Proper use of fire safety equipment</li>
                  <li>Emergency contact numbers displayed</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.e3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.e3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="e3"
                      value={option}
                      checked={form.e3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.e3 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.e3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.e3}
                </p>
              )}
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for comprehensive safety assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_e}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            if (file.type.startsWith("image/")) {
              const previewUrl = URL.createObjectURL(file);
              setPreviewImages((prev) => ({
                ...prev,
                [cameraField]: previewUrl,
              }));
            }

            setShowCamera(false);
            setCameraField(null);
          }}
          onClose={() => {
            setShowCamera(false);
            setCameraField(null);
          }}
        />
      )}
    </div>
  );
};

export default SectionESafety;